import React from 'react';

// Bildirimlerin metadata'sındaki yapılandırılmış değişiklikleri seçili dile göre gösterir ve
// bildirime tıklanınca ilgili kaydı açar. Header'daki Bildirim Merkezi ve Dashboard'daki
// Son Aktiviteler aynı yardımcıları kullanır.

type Translate = (key: string, params?: Record<string, string | number>) => string;

export interface FieldChange {
  field: string;
  old?: string | number | null;
  new?: string | number | null;
  old_label?: string | null;
  new_label?: string | null;
}

interface EntityMetadata {
  entity?: string;
  entity_id?: number;
  action?: 'create' | 'update' | 'delete' | 'archive' | 'unarchive';
  device?: string;
  inventory_number?: string;
  changes?: FieldChange[];
  // Toplu arşiv bildirimleri
  count?: number;
  unassigned_count?: number;
  gsm_released_count?: number;
  reason?: string | null;
  inventory_numbers?: string[];
  // GSM hattı cihaz değişikliği
  phone_number?: string;
  from_phone?: string | null;
  to_phone?: string | null;
  [key: string]: unknown;
}

interface NotificationLike {
  title: string;
  message: string;
  action_url?: string | null;
  metadata?: unknown;
}

export interface FormattedChange {
  label: string;
  oldText: string | null;
  newText: string | null;
  // Eski → yeni yerine düz bilgi satırı (ör. toplu arşivde envanter listesi)
  text?: string;
}

export const parseNotificationMetadata = (metadata: unknown): EntityMetadata | null => {
  if (!metadata) return null;
  if (typeof metadata === 'object') return metadata as EntityMetadata;
  try {
    const parsed = JSON.parse(String(metadata));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const DATE_FIELDS = ['creation_date', 'contract_start_date', 'contract_end_date'];
const MONEY_FIELDS = ['contract_price', 'monthly_payment'];

const formatChangeValue = (
  change: FieldChange,
  side: 'old' | 'new',
  t: Translate,
  locale: string
): string => {
  const value = change[side];
  if (value === null || value === undefined || value === '') return '—';

  const label = change[`${side}_label`];
  if (label) return label;

  if (change.field === 'status' || change.field === 'ownership') {
    const key = `notif.change.${change.field}.${value}`;
    const translated = t(key);
    return translated === key ? String(value) : translated;
  }
  if (DATE_FIELDS.includes(change.field)) {
    const [y, m, d] = String(value).split('-').map(Number);
    return d ? new Date(y, m - 1, d).toLocaleDateString(locale) : String(value);
  }
  if (MONEY_FIELDS.includes(change.field) && !isNaN(Number(value))) {
    return Number(value).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
};

const isMobilePhoneEntity = (meta: EntityMetadata | null): meta is EntityMetadata =>
  !!meta && meta.entity === 'mobile_phone' && !!meta.action;

const isGsmLineEntity = (meta: EntityMetadata | null): boolean =>
  !!meta && meta.entity === 'gsm_line' && !!meta.phone_number;

export const getNotificationDisplay = (
  notification: NotificationLike,
  t: Translate,
  language: string
): { title: string; message: string; changes: FormattedChange[] } => {
  const meta = parseNotificationMetadata(notification.metadata);
  if (meta && isGsmLineEntity(meta)) {
    const changes: FormattedChange[] = [{
      label: t('notif.gsm.phone'),
      oldText: meta.from_phone || '—',
      newText: meta.to_phone || t('notif.gsm.removed')
    }];
    if (meta.reason) changes.push({ label: t('notif.change.reason'), oldText: null, newText: null, text: meta.reason });
    return {
      title: t('notif.gsm.deviceChange.title'),
      message: t('notif.gsm.deviceChange.message', { number: meta.phone_number || '' }),
      changes
    };
  }
  if (!isMobilePhoneEntity(meta)) {
    // Eski veya yapılandırılmamış bildirimler olduğu gibi gösterilir
    return { title: notification.title, message: notification.message, changes: [] };
  }

  const params = { device: meta.device || '', inventory: meta.inventory_number || '', count: meta.count ?? 0 };

  if (meta.action === 'archive' || meta.action === 'unarchive') {
    const info: FormattedChange[] = [];
    if (meta.inventory_numbers?.length) {
      const extra = (meta.count ?? 0) - meta.inventory_numbers.length;
      info.push({
        label: t('notif.change.field.inventory_number'), oldText: null, newText: null,
        text: meta.inventory_numbers.join(', ') + (extra > 0 ? ` ${t('notif.change.more', { count: extra })}` : '')
      });
    }
    if (meta.reason) info.push({ label: t('notif.change.reason'), oldText: null, newText: null, text: meta.reason });
    if (meta.gsm_released_count) {
      info.push({ label: t('notif.change.field.gsm_number'), oldText: null, newText: null, text: t('notif.change.gsmReleased', { count: meta.gsm_released_count }) });
    }
    if (meta.unassigned_count) {
      info.push({ label: t('notif.change.field.assigned_user_id'), oldText: null, newText: null, text: t('notif.change.unassigned', { count: meta.unassigned_count }) });
    }
    return {
      title: t(`notif.mobile.${meta.action}.title`),
      message: t(`notif.mobile.${meta.action}.message`, params),
      changes: info
    };
  }
  return {
    title: t(`notif.mobile.${meta.action}.title`),
    message: t(`notif.mobile.${meta.action}.message`, params),
    changes: formatFieldChanges(meta.changes || [], t, language)
  };
};

// Cep telefonu alan değişikliklerini (eski → yeni) seçili dile göre etiketler.
// Bildirimler ve toplu içe aktarma önizlemesi kullanır.
export const formatFieldChanges = (changes: FieldChange[], t: Translate, language: string): FormattedChange[] => {
  const locale = language === 'en' ? 'en-US' : 'tr-TR';
  return changes.map(change => {
    const labelKey = `notif.change.field.${change.field}`;
    const label = t(labelKey) === labelKey ? change.field : t(labelKey);
    // Notlar gibi değeri saklanmayan alanlar sadece "güncellendi" olarak gösterilir
    if (!('old' in change) && !('new' in change)) {
      return { label, oldText: null, newText: null };
    }
    return {
      label,
      oldText: formatChangeValue(change, 'old', t, locale),
      newText: formatChangeValue(change, 'new', t, locale)
    };
  });
};

// Bildirimin işaret ettiği sayfaya gider; action_url'de kayıt ID'si varsa o kaydı açtırır.
// Gidilecek bir hedef yoksa false döner.
export const openNotificationTarget = (notification: NotificationLike): boolean => {
  if (!notification.action_url) return false;

  // action_url formatı: /page veya /page/entityId
  const parts = notification.action_url.replace(/^\//, '').split('/');
  const page = parts[0];
  const entityId = parts[1] ? parseInt(parts[1]) : null;

  if (entityId && page === 'notepad') {
    // Not defteri: URL param ile önizleme aç
    window.history.replaceState(null, '', `/notepad?note=${entityId}`);
    window.dispatchEvent(new CustomEvent('navigate', { detail: 'notepad' }));
  } else if (entityId) {
    // Önce sayfaya git, sonra 300ms bekleyerek openEntityById gönder.
    // React'ın ilgili sayfayı mount edip event listener'ı kaydetmesi için
    // bekleme şart — aksi hâlde event listener henüz yokken event gönderilir.
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openEntityById', {
        detail: { page, entityId }
      }));
    }, 300);
  } else {
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
  }
  return true;
};

interface NotificationChangeListProps {
  changes: FormattedChange[];
  t: Translate;
  maxItems?: number;
  className?: string;
}

export const NotificationChangeList: React.FC<NotificationChangeListProps> = ({ changes, t, maxItems, className = '' }) => {
  if (changes.length === 0) return null;
  const visible = maxItems ? changes.slice(0, maxItems) : changes;
  const hiddenCount = changes.length - visible.length;

  return (
    <ul className={`space-y-0.5 ${className}`}>
      {visible.map((change, idx) => (
        <li key={idx} className="flex flex-wrap items-baseline gap-x-1 break-words">
          <span className="font-medium text-gray-700 dark:text-gray-300">{change.label}:</span>
          {change.text !== undefined ? (
            <span className="text-gray-700 dark:text-gray-300">{change.text}</span>
          ) : change.oldText === null ? (
            <span className="text-gray-500 dark:text-gray-400">{t('notif.change.updated')}</span>
          ) : (
            <>
              <span className="text-gray-500 dark:text-gray-400 line-through decoration-gray-400/60">{change.oldText}</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-900 dark:text-gray-100">{change.newText}</span>
            </>
          )}
        </li>
      ))}
      {hiddenCount > 0 && (
        <li className="text-gray-400">{t('notif.change.more', { count: hiddenCount })}</li>
      )}
    </ul>
  );
};
